import React, { useEffect, useState } from "react";
import {
  Button,
  Upload,
  message,
  Table,
  Modal,
  Card,
  Layout,
  Popconfirm,
  Spin,
  Input,
  Tooltip,
} from "antd";
import { PlusOutlined, PlayCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import API_BASE from "../config/api";
import { validatePromoUploadFile, isVideoFile } from "../config/uploadLimits";
import {
  getUploadErrorMessage,
  updatePromoWithVideo,
} from "../utils/promoUploadApi";

const { Content, Header } = Layout;

export default function PromoAdsPage() {
  const { modeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const customer_id = location.state?.customer_id || null;

  const [promos, setPromos] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [commonTitle, setCommonTitle] = useState("");

  const [viewVisible, setViewVisible] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [editVisible, setEditVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editImage, setEditImage] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPreview, setEditPreview] = useState(null);

  useEffect(() => {
    if (!modeId) {
      message.error("Mode ID missing. Create a mode first.");
      navigate("/admin/mode");
      return;
    }
    fetchPromos();
  }, [modeId]);

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/fetchPromosByMode/${modeId}`);
      if (data.success) setPromos(data.data);
    } catch (err) {
      console.error(err);
      message.error("Failed to load promos");
    } finally {
      setLoading(false);
    }
  };

  // ------------------ IMAGE PROCESSING ------------------
  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;
        let rotate = false;

        if (height > width) {
          rotate = true;
          [width, height] = [height, width];
        }

        canvas.width = 1920;
        canvas.height = 1080;

        if (rotate) {
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((90 * Math.PI) / 180);
          ctx.drawImage(img, -height / 2, -width / 2, height, width);
          ctx.restore();
        } else {
          const scale = Math.min(1920 / width, 1080 / height);
          const newWidth = width * scale;
          const newHeight = height * scale;
          const dx = (1920 - newWidth) / 2;
          const dy = (1080 - newHeight) / 2;
          ctx.drawImage(img, dx, dy, newWidth, newHeight);
        }

        canvas.toBlob(
          (blob) => {
            const newFile = new File([blob], file.name, { type: file.type });
            newFile.uid = file.uid;
            resolve(newFile);
          },
          file.type,
          0.95
        );
      };

      img.onerror = () => {
        message.error(`Failed to process ${file.name}`);
        reject();
      };
    });
  };

    // ------------------ BULK UPLOAD ------------------
  const beforeUpload = async (file) => {
    const isOver100MB = file.size / 1024 / 1024 > 100;
    if (isOver100MB) {
      message.error(`${file.name} exceeds 100MB limit.`);
      return Upload.LIST_IGNORE;
    }

    if (file.type.startsWith("video/")) {
      setFileList((prev) => [...prev, file]);
    } else {
      const processed = await processImage(file);
      setFileList((prev) => [...prev, processed]);
    }
    return false; // prevent auto upload
  };

  const handleRemoveLocal = (file) => {
    setFileList((prev) => prev.filter((f) => f.uid !== (file.uid || file.name)));
  };

  const handleBulkUpload = async () => {
    if (!fileList.length) {
      message.warning("Select files first");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      fileList.forEach((file) => formData.append("file", file));
      formData.append("modeId", modeId);
      formData.append("customerId", customer_id);
      formData.append("title", commonTitle.trim() || "None");

      const res = await axios.post(`${API_BASE}/addPromoAds`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        message.success("Uploaded successfully");
        setFileList([]);
        setCommonTitle("");
        fetchPromos();
      } else {
        message.error(res.data.message || "Upload failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Upload error");
    } finally {
      setUploading(false);
    }
  };
  // ------------------ EDIT MODAL ------------------
  const openEditModal = (record) => {
    setEditData(record);
    setEditTitle(record.title || "");
    setEditImage(null);
    setEditPreview(record.S3link || null);
    setEditVisible(true);
  };

  const handleEditImageUpload = async ({ file }) => {
    const validation = validatePromoUploadFile(file);
    if (!validation.ok) {
      message.error(validation.message);
      return false;
    }

    if (file.type && file.type.startsWith("video/")) {
      setEditImage(file);
      setEditPreview(URL.createObjectURL(file));
      return false;
    }

    const processed = await processImage(file);
    setEditImage(processed);

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setEditPreview(e.target.result);
    reader.readAsDataURL(processed);

    return false; // prevent default Upload
  };

  const handleUpdatePromo = async () => {
    if (!editData) return;

    const title = editTitle.trim() || "None";

    try {
      if (editImage && isVideoFile(editImage)) {
        await updatePromoWithVideo({
          file: editImage,
          customerId: customer_id,
          promoId: editData._id,
          title,
          apiBase: API_BASE,
        });
      } else {
        const formData = new FormData();
        formData.append("_id", editData._id);
        formData.append("title", title);
        if (editImage) formData.append("file", editImage);

        const res = await axios.post(`${API_BASE}/editPromo`, formData, {
          timeout: 120000,
        });

        if (!res.data.success) {
          message.error(res.data.message || "Update failed");
          return;
        }
      }

      message.success("Updated Successfully");
      setEditVisible(false);
      setEditImage(null);
      setEditPreview(null);
      fetchPromos();
    } catch (err) {
      console.error(err);
      message.error(getUploadErrorMessage(err));
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.post(`${API_BASE}/deletePromo`, { _id: id });
      if (res.data.success) {
        message.success("Deleted");
        fetchPromos();
      }
    } catch (err) {
      console.error(err);
      message.error("Delete error");
    }
  };

  const columns = [
    { title: "Title", dataIndex: "title", render: (v) => v || "None" },
    {
      title: "Content",
      dataIndex: "S3link",
      render: (url, record) =>
        url ? (
          (record.file_type === "video" || url.match(/\.(mp4|webm|ogg|mov)$/i)) ? (
            <div style={{ position: "relative", width: 80, height: 80 }}>
              <video src={url} style={{ width: 80, height: 80, objectFit: "cover" }} />
              <PlayCircleOutlined
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: 24,
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                }}
              />
            </div>
          ) : (
            <img src={url} style={{ width: 80, height: 80, objectFit: "cover" }} />
          )
        ) : (
          "—"
        ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="link" onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Button
            type="link"
            onClick={() => {
              setViewData(record);
              setViewVisible(true);
            }}
          >
            View
          </Button>
          <Popconfirm title="Delete this promo?" onConfirm={() => handleDelete(record._id)}>
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="app-container-page">
      <Layout className="admins" style={{ minHeight: "100vh" }}>
        <Header style={{ background: "#fff", padding: "0 20px" }}>
          <div className="header_alignDivs">
            <h3>Promo Ads</h3>
            <Button onClick={() => navigate("/admin/mode", { state: { customer_id } })}>Back</Button>
          </div>
        </Header>

        <Content className="boxMargins">
          <div className="topConts-ads">
            <div className="titleSections">
              <Input
                placeholder="Enter common title (Optional)"
                value={commonTitle}
                onChange={(e) => setCommonTitle(e.target.value)}
                style={{ width: 300 }}
              />
            </div>

            <div className="imgs-sect">
              <Upload
                multiple
                beforeUpload={beforeUpload}
                fileList={fileList.map((f) => ({ uid: f.uid || f.name, name: f.name }))} //remove ||f.name
                onRemove={handleRemoveLocal}
                accept="image/*,video/*"
                showUploadList={{ showPreviewIcon: false, showDownloadIcon: false }}
              >
                <Tooltip title="Image and Video">
                  <Button icon={<PlusOutlined />}>Select Content</Button>
                </Tooltip>
              </Upload>

              <Button type="primary" onClick={handleBulkUpload} loading={uploading}>
                Upload ({fileList.length})
              </Button>
            </div>
          </div>

          {loading ? (
            <Spin />
          ) : (
            <Table rowKey="_id" dataSource={promos} columns={columns} bordered />
          )}
        </Content>

        {/* VIEW MODAL */}
        <Modal
          open={viewVisible}
          footer={null}
          onCancel={() => setViewVisible(false)}
          width={800}
          centered
        >
          {viewData && (
            <div style={{ textAlign: "center", padding: "10px" }}>
              {viewData.file_type === "video" || viewData.S3link?.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                <video
                  src={viewData.S3link}
                  controls
                  autoPlay
                  style={{ width: "100%", maxHeight: "70vh", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={viewData.S3link}
                  alt={viewData.title || "Promo Ad"}
                  style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "8px" }}
                />
              )}

              <div style={{ marginTop: "15px", fontWeight: "bold", fontSize: "16px" }}>
                {viewData.title || "Untitled Promo"}
              </div>
            </div>
          )}
        </Modal>

        {/* EDIT MODAL */}
        <Modal className="modelPrev" open={editVisible} title="" onCancel={() => setEditVisible(false)} onOk={handleUpdatePromo}>
          <p>Title:</p>
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Enter title"
            style={{ marginBottom: 12 }}
          />

          <p>Content Preview:</p>
          {editPreview && (
            (editImage?.type?.startsWith("video/") || editData?.file_type === "video" || editPreview?.match(/\.(mp4|webm|ogg|mov)$/i)) ? (
              <video
                src={editPreview}
                controls
                style={{ width: "100%", height: 200, objectFit: "cover", borderRadius: 8, marginBottom: 10 }}
              />
            ) : (
              <img
                src={editPreview}
                style={{ width: "100%", height: 200, objectFit: "cover", borderRadius: 8, marginBottom: 10 }}
              />
            )
          )}

          <Upload beforeUpload={() => false} onChange={handleEditImageUpload} maxCount={1} accept="image/*,video/*" showUploadList={false}>
            <Tooltip title="Image and Video">
              <Button icon={<PlusOutlined />}>Upload New Content</Button>
            </Tooltip>
          </Upload>
        </Modal>
      </Layout>
    </div>
  );
}